/**
 * Types et interfaces pour les widgets Apidae
 */

// Types importés depuis apidae.d.ts
import { ElementReference, Commune, FacetId, FacetValue } from "./apidae.d";

/**
 * Types de templates disponibles
 */
export enum WidgetTemplateType {
  LIST = "LIST",
  AGENDA = "AGENDA",
  MAP = "MAP",
  CAROUSEL = "CAROUSEL",
  LIST_MAP = "LIST_MAP",
  AGENDA_MAP = "AGENDA_MAP",
}

/**
 * Langues disponibles (11 langues Apidae)
 */
export enum WidgetLanguage {
  FR = "fr",
  EN = "en",
  ES = "es",
  IT = "it",
  DE = "de",
  NL = "nl",
  RU = "ru",
  ZH = "zh",
  PT_BR = "pt-BR",
  JA = "ja",
  KO = "ko",
}

/**
 * Polices disponibles
 */
export enum WidgetFont {
  ROBOTO = "Roboto",
  OPEN_SANS = "Open Sans",
  INTER = "Inter",
  MONTSERRAT = "Montserrat",
  LATO = "Lato",
  POPPINS = "Poppins",
  DM_SANS = "DM Sans",
  HELVETICA = "Helvetica",
  ARIAL = "Arial",
  INHERIT = "inherit",
}

/**
 * Options de tri pour les listes
 */
export enum WidgetSortOption {
  IDENTIFIANT = "IDENTIFIANT",
  NOM = "NOM",
  PERTINENCE = "PERTINENCE",
  DISTANCE = "DISTANCE",
  RANDOM = "RANDOM",
  DATE_OUVERTURE = "DATE_OUVERTURE",
}

export type WidgetSortDirection = "asc" | "desc";

export interface WidgetSortConfig {
  option: WidgetSortOption;
  order: WidgetSortDirection;
}

/**
 * Configuration des couleurs
 */
export interface WidgetColors {
  primary: string;
  secondary: string;
}

/**
 * Configuration des polices
 */
export interface WidgetTypography {
  titleFont: WidgetFont;
  titleColor: string;
  titleSize?: number; // en px // champ non modifiable dans le BO actuellement
  textFont: WidgetFont;
  textColor: string;
  textSize?: number; // en px // champ non modifiable dans le BO actuellement
}

/**
 * Configuration du périmètre géographique par liste de communes
 */
export interface GeographicPerimeterCommunes {
  type: "communes";
  communeIds: string[];
}

/**
 * Configuration du périmètre géographique par rayon
 */
export interface GeographicPerimeterRadius {
  type: "radius";
  latitude: number;
  longitude: number;
  /** Rayon en mètres */
  radius: number;
}

/**
 * Périmètre géographique (union type)
 */
export type GeographicPerimeter =
  | GeographicPerimeterCommunes
  | GeographicPerimeterRadius
  | null;

/**
 * Configuration de personnalisation graphique
 */
export interface WidgetVisualCustomization {
  colors: WidgetColors;
  typography: WidgetTypography;
  customHTML?: string;
  customCSS?: string;
  logo?: string; // URL ou base64
  logoUrl?: string; //URL au clic sur le logo
}

/**
 * Configuration de personnalisation du contenu
 */
export interface WidgetContentCustomization {
  language: WidgetLanguage;
  aspectId?: string;
  geographicPerimeter?: GeographicPerimeter;
  excludedOfferIds: string[];
  displayedFields: WidgetFieldsInfos[]; // IDs des champs à afficher
  filterFields: WidgetFieldsInfos[]; // IDs des champs disponibles dans les filtres
  displayedFieldsPreview?: WidgetFieldsInfos[]; // IDs des champs disponibles à l'affichage de la minicard
}

export interface WidgetFieldsInfos {
  id: string; // FilterId, voire plus, car aussi utilisé par displayedFields, en plus de filterFields ?
  data?: any; // pour les champs spécifiques à des offres (ex: disponibilitesElloha, disponibilitesOpensystem)
}

/**
 * Sélection Apidae (format API backend)
 */
export interface ApidaeSelectionResponse {
  id: number;
  libelle: {
    libelleFr: string;
  };
  nom: string;
}

/**
 * Projet avec ses sélections (format API backend)
 */
export interface ProjetSelections {
  projetId: number;
  selections?: ApidaeSelectionResponse[];
}

/**
 * Sélection Apidae (format utilisé dans le widget)
 */
export interface ApidaeSelection {
  id: string;
  label: string;
  count?: number; // Nombre de fiches
}

/**
 * Configuration complète d'un widget
 */
export interface WidgetConfiguration {
  // Informations obligatoires
  name: string;
  templateType: WidgetTemplateType;
  selections: ApidaeSelection[];
  targetUrl: string;

  // Informations Apidae pour récupérer les données
  projetId: string;

  // Personnalisation
  visualCustomization: WidgetVisualCustomization;
  contentCustomization: WidgetContentCustomization;

  /** Tri des offres (éditorial, non modifiable par le visiteur). */
  sort?: WidgetSortConfig;

  // Paramètres spécifiques au template
  templateSpecificConfig?: TemplateSpecificConfig;
}

/**
 * Configuration spécifique au template Carrousel
 */
export interface CarouselConfig {
  itemsToShow: 1 | 3 | 5;
  autoPlay?: boolean;
  autoPlayInterval?: number; // en millisecondes
}

/**
 * Configuration spécifique au template Liste
 */
export interface ListConfig {
  itemsPerPage?: number;
  showFilters?: boolean;
}

/**
 * Configuration spécifique au template Agenda
 */
export interface AgendaConfig {
  showFilters: boolean;
  groupByDate: boolean;
}

/**
 * Configuration spécifique au template Carte
 */
export interface MapConfig {
  hoverFields: string[]; // IDs des champs affichés au survol
  defaultZoom?: number;
}

/**
 * Union type pour les configurations spécifiques
 */
export type TemplateSpecificConfig = {
  carousel?: {
    nbItemsToShow?: number; // 1 | 3 | 5
  };
  [key: string]: any; // Allow unknown properties for flexibility
};

/**
 * Widget complet (entité persistée)
 */
export interface Widget {
  id: string;
  memberId: string; // ID du membre propriétaire
  configuration: WidgetConfiguration;
  createdAt: string;
  updatedAt: string;
  createdBy: string; // ID de l'utilisateur créateur
  updatedBy: string; // ID du dernier utilisateur modificateur
}

/**
 * Types d'identifiants de filtres disponibles (correspond à AVAILABLE_FILTERS dans apidae-sit-gui)
 */
export enum FilterId {
  DUREE = "duree", // intervalles
  GRATUITE = "gratuite",
  ANIMAUX = "animaux",
  CLASSEMENT = "classement", // élément de référence, classes : DistinctionNom, DistinctionQualification (avec la DistinctionCategorie CLASSEMENTS)
  COMMUNES = "communes", // communes
  ACTIVITES = "activites", // élément de référence, classes : ActiviteSportivePrestation, ActiviteCulturellePrestation, PrestationActivite
  TYPES = "types", // élément de référence, classes : ActiviteType, CommerceEtServiceType, DomaineSkiableClassification, FeteEtManifestationType, EquipementRubrique, HebergementCollectifType, HebergementLocatifType, HotelleriePleinAirType, HotellerieType, PatrimoineCulturelType, PatrimoineNaturelCategorie, DegustationTypeProduit, RestaurationType, ActiviteCulturellePrestation (du champ ((SejourPackage) ot).getInformationsSejourPackage().getActivitesCulturelles()), ActiviteSportivePrestation (du champ ((SejourPackage) ot).getInformationsSejourPackage().getActivitesSportives())
  PUBLIC = "public", // élément de référence, classes : TypeClientele
  LABELS = "labels", // élément de référence, classes : DistinctionNom, DistinctionQualification (avec la DistinctionCategorie LABELS)
  DISTANCE = "distance", // intervalles
  OUVERTURE = "ouverture", // dates
}

/**
 * Types d'identifiants de champs disponibles (correspond à AVAILABLE_FIELDS dans apidae-sit-gui)
 */
export enum DisplayedFieldId {
  DESCRIPTIF_COURT = "descriptifCourt",
  DESCRIPTIF_DETAILLE = "descriptifDetaille",
  IMAGES = "images",
  MULTIMEDIAS = "multimedias",
  MOYENS_COMMUNICATION = "moyensCommunication",
  OUVERTURE = "ouverture",
  TARIFS = "tarifs",
  MOYEN_PAIEMENT = "moyenPaiement",
  INFORMATIONS_RESERVATION = "informationsReservation",
  EQUIPEMENTS = "equipements",
  LABEL_DISTINCTION = "labelDistinction",
  ACCESSIBILITE = "accessibilite",
  INFOS_ITINERAIRES = "infosItineraires",
  CAPACITES = "capacites",
  LOCATION_SALLE = "locationSalle",
  ANIMAUX_ACCEPTES = "animauxAcceptes",
  DESCRIPTIFS_THEMATISES = "descriptifsThematises",
  DESCRIPTIFS_PRIVES = "descriptifsPrives",
  PUBLIC_NIVEAU = "publicNiveau",
  OBJETS_LIES = "objetsLies",
  PRESTATAIRE_ACTIVITE = "prestataireActivite",
  DISPONIBILITES_ELLOHA = "disponibilitesElloha",
  DISPONIBILITES_OPENSYSTEM = "disponibilitesOpensystem",
}

/**
 * Types d'identifiants de champs preview disponibles (correspond à AVAILABLE_FIELDS_PREVIEW dans apidae-sit-gui)
 */
export enum DisplayedFieldPreviewId {
  COMMUNE = "commune",
  OUVERTURE = "ouverture",
  TELEPHONE = "telephone",
  CATEGORIE = "categorie",
  RESERVATION = "reservation",
}

export const FILTER_DEFINITIONS: Record<FilterId, FilterDefinition> = {
  [FilterId.DUREE]: {
    id: FilterId.DUREE,
    facet: FacetId.DUREE_SEANCE,
  },
  [FilterId.GRATUITE]: {
    id: FilterId.GRATUITE,
    facet: FacetId.GRATUIT,
  },
  [FilterId.ANIMAUX]: {
    id: FilterId.ANIMAUX,
    facet: FacetId.ANIMAUX,
  },
  [FilterId.CLASSEMENT]: {
    id: FilterId.CLASSEMENT,
    facet: FacetId.DISTINCTION_CLASSEMENT,
  },
  [FilterId.COMMUNES]: {
    id: FilterId.COMMUNES,
    facet: FacetId.COMMUNE,
  },
  [FilterId.ACTIVITES]: {
    id: FilterId.ACTIVITES,
    facet: FacetId.ACTIVITE,
  },
  [FilterId.TYPES]: {
    id: FilterId.TYPES,
    facet: FacetId.SOUS_TYPE,
  },
  [FilterId.PUBLIC]: {
    id: FilterId.PUBLIC,
    facet: FacetId.TYPE_CLIENTELE,
  },
  [FilterId.LABELS]: {
    id: FilterId.LABELS,
    facet: FacetId.DISTINCTION_LABEL,
  },
  [FilterId.DISTANCE]: {
    id: FilterId.DISTANCE,
    facet: FacetId.EQU_DISTANCE_ITINERAIRE,
  },
  [FilterId.OUVERTURE]: {
    id: FilterId.OUVERTURE,
    facet: undefined,
  },
};
